import React, { useCallback, useEffect, useState } from "react";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITY } from "../../helpers/permissions";
import {
  disableProjectShare,
  enableProjectShare,
  getProjectShare,
  regenerateProjectShare,
  setShareBilling,
  setShareDescription,
} from "../../helpers/clientView/clientViewShare";
import {
  buildClientViewUrl,
  friendlyError,
  pictureCountLabel,
  shareStatusLabel,
} from "../../helpers/clientView/clientViewModel";
import { isOffline } from "../../helpers/connectivity";
import { RecBar, RecIcon } from "../materials/MaterialRecordParts";
import { ClientPicturesManageScreen } from "./ClientPicturesManageScreen";
import { ClientConversationScreen } from "./ClientConversationScreen";
import "./clientViewManage.css";

async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

/**
 * Project → Client View. Small on purpose: status, link actions, what is
 * shared, and a subordinate security area.
 */
export function ClientViewManageScreen({ projectId, projectName, writable = true, onBack, onChanged }) {
  const { can, actor } = usePermissions();
  const allowed = can(CAPABILITY.PROJECT_CLIENT_VIEW_MANAGE);
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [view, setView] = useState("main");
  const [description, setDescription] = useState("");
  const offline = isOffline();

  const apply = useCallback(
    (next) => {
      setShare(next);
      setDescription(next?.client_description || "");
      onChanged?.(next);
    },
    [onChanged],
  );

  const load = useCallback(async () => {
    setError("");
    try {
      apply(await getProjectShare(actor, projectId));
    } catch (err) {
      setError(friendlyError(err, "Unable to load Client View."));
    } finally {
      setLoading(false);
    }
  }, [actor, projectId, apply]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function run(work, success = "") {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await work();
      if (success) setNotice(success);
      return result;
    } catch (err) {
      setError(friendlyError(err));
      return undefined;
    } finally {
      setBusy(false);
    }
  }

  const url = share?.token ? buildClientViewUrl(share.token) : "";

  async function handleCopy() {
    const ok = await copyText(url);
    setNotice(ok ? "Link copied." : "Could not copy. Press and hold the link to copy it.");
  }

  async function handleShare() {
    const data = {
      title: projectName || "Project",
      text: `Here is the link to follow ${projectName || "your project"}:`,
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(data);
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    await handleCopy();
  }

  if (view === "pictures") {
    return (
      <ClientPicturesManageScreen
        projectId={projectId}
        projectName={projectName}
        writable={writable}
        onBack={() => {
          setView("main");
          load();
        }}
      />
    );
  }

  if (view === "conversation") {
    return (
      <ClientConversationScreen
        projectId={projectId}
        projectName={projectName}
        writable={writable}
        onBack={() => {
          setView("main");
          load();
        }}
      />
    );
  }

  const active = Boolean(share?.is_active);

  return (
    <div className="screen nx-screen rec-page" data-testid="client-view-manage">
      <RecBar title="Client View" onBack={onBack} />
      {projectName ? <div className="tk-subtitle">{projectName}</div> : null}

      <div className="rec-body">
        {!allowed ? (
          <div className="rec-banner" role="alert">
            Only a manager or admin can share a project.
          </div>
        ) : null}
        {error ? (
          <div className="rec-banner" role="alert">
            {error}
          </div>
        ) : null}
        {notice ? (
          <div className="cvm-notice" role="status">
            {notice}
          </div>
        ) : null}
        {offline ? (
          <div className="tk-notice" role="status">
            Reconnect to manage Client View.
          </div>
        ) : null}
        {!writable ? (
          <div className="tk-notice" role="status">
            This project is archived. The current link keeps working until you disable it, but sharing cannot be
            changed.
          </div>
        ) : null}

        <section className="rec-card cvm-status">
          <div className="cvm-status__row">
            <span className="cvm-status__label">Status</span>
            <span className={`tk-chip tk-chip--status${active ? " is-done" : ""}`} data-testid="client-view-status">
              {loading ? "…" : shareStatusLabel(share)}
            </span>
          </div>

          {!loading && !active ? (
            <>
              <p className="cvm-help">
                Share a controlled view of this project with your customer. They open a link on their phone — no
                account needed. They see the overview, locations and dimensions, and only the pictures you choose.
                Costs and internal notes are never included. Billing stays off until you turn it on.
              </p>
              <button
                type="button"
                className="rec-btn rec-btn--primary"
                disabled={busy || offline || !writable || !allowed}
                onClick={() => run(async () => apply(await enableProjectShare(actor, projectId)))}
              >
                <RecIcon name="link" />
                Enable Client View
              </button>
            </>
          ) : null}

          {active ? (
            <>
              <div className="cvm-link" data-testid="client-view-link">
                <RecIcon name="link" />
                <span>{url}</span>
              </div>
              <div className="cvm-actions">
                <button type="button" className="rec-btn rec-btn--primary" onClick={handleCopy} disabled={busy}>
                  <RecIcon name="copy" />
                  Copy Link
                </button>
                <button type="button" className="rec-btn rec-btn--outline" onClick={handleShare} disabled={busy}>
                  <RecIcon name="share" />
                  Share
                </button>
              </div>
              <a className="rec-btn rec-btn--ghost cvm-preview" href={url} target="_blank" rel="noopener noreferrer">
                <RecIcon name="eye" />
                Preview Client View
              </a>
            </>
          ) : null}
        </section>

        {active ? (
          <>
            <div className="pj-label">Shared content</div>
            <div className="pj-list">
              <div className="pj-navrow cvm-static">
                <span className="pj-navrow__icon">
                  <RecIcon name="doc" />
                </span>
                <span className="pj-navrow__text">
                  <span className="pj-navrow__title">Overview</span>
                  <span className="pj-navrow__sub">Always included</span>
                </span>
              </div>
              <div className="pj-navrow cvm-static">
                <span className="pj-navrow__icon">
                  <RecIcon name="pin" />
                </span>
                <span className="pj-navrow__text">
                  <span className="pj-navrow__title">Locations &amp; Dimensions</span>
                  <span className="pj-navrow__sub">Always included</span>
                </span>
              </div>
              <button type="button" className="pj-navrow" onClick={() => setView("pictures")}>
                <span className="pj-navrow__icon">
                  <RecIcon name="image" />
                </span>
                <span className="pj-navrow__text">
                  <span className="pj-navrow__title">Pictures</span>
                  <span className="pj-navrow__sub" data-testid="client-view-picture-count">
                    {pictureCountLabel(share.shared_picture_count)}
                  </span>
                </span>
                <RecIcon name="chevron" className="pj-navrow__chev" />
              </button>
              <div className="pj-navrow cvm-static">
                <span className="pj-navrow__icon">
                  <RecIcon name="invoice" />
                </span>
                <span className="pj-navrow__text">
                  <span className="pj-navrow__title">Billing</span>
                  <span className="pj-navrow__sub">{share.share_billing ? "Shared with the client" : "Not shared"}</span>
                </span>
                <button
                  type="button"
                  className={`cvm-switch${share.share_billing ? " is-on" : ""}`}
                  role="switch"
                  aria-checked={share.share_billing ? "true" : "false"}
                  aria-label="Share billing with the client"
                  data-testid="client-view-billing-toggle"
                  disabled={busy || offline || !writable || !allowed}
                  onClick={() =>
                    run(async () => apply(await setShareBilling(actor, projectId, !share.share_billing)))
                  }
                />
              </div>
              <button type="button" className="pj-navrow" onClick={() => setView("conversation")}>
                <span className="pj-navrow__icon">
                  <RecIcon name="chat" />
                </span>
                <span className="pj-navrow__text">
                  <span className="pj-navrow__title">Client Conversation</span>
                  <span className="pj-navrow__sub">
                    {share.message_count
                      ? `${share.message_count} message${share.message_count === 1 ? "" : "s"}`
                      : "Enabled"}
                  </span>
                </span>
                <RecIcon name="chevron" className="pj-navrow__chev" />
              </button>
            </div>

            <div className="pj-label">About this project (shown to your client)</div>
            <section className="rec-card cvm-desc">
              <textarea
                className="rec-input rec-input--area"
                rows={3}
                maxLength={2000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A short note for your customer. Internal project notes are never shown."
                aria-label="Description for the client"
                disabled={!writable || offline}
              />
              <button
                type="button"
                className="rec-btn rec-btn--outline"
                disabled={busy || !writable || offline || description === (share.client_description || "")}
                onClick={() => run(async () => apply(await setShareDescription(actor, projectId, description)), "Saved.")}
              >
                Save
              </button>
            </section>

            <div className="pj-label">Access</div>
            <div className="pj-list">
              <button
                type="button"
                className="pj-navrow"
                disabled={busy || offline || !writable}
                onClick={() => {
                  if (
                    window.confirm(
                      "Create a new link? The current link stops working immediately and anyone who has it will need the new one.",
                    )
                  ) {
                    run(async () => apply(await regenerateProjectShare(actor, projectId)), "New link created. The old link no longer works.");
                  }
                }}
              >
                <span className="pj-navrow__icon">
                  <RecIcon name="link" />
                </span>
                <span className="pj-navrow__text">
                  <span className="pj-navrow__title">Create a new link</span>
                  <span className="pj-navrow__sub">Turns the current link off</span>
                </span>
              </button>
              <button
                type="button"
                className="pj-navrow cvm-danger"
                disabled={busy || offline}
                onClick={() => {
                  if (
                    window.confirm(
                      "Disable client access? The link stops working immediately. You can enable it again later with a new link.",
                    )
                  ) {
                    run(async () => {
                      await disableProjectShare(actor, projectId);
                      apply(null);
                    }, "Client access disabled. The old link no longer works.");
                  }
                }}
              >
                <span className="pj-navrow__icon">
                  <RecIcon name="close" />
                </span>
                <span className="pj-navrow__text">
                  <span className="pj-navrow__title">Disable Client Access</span>
                  <span className="pj-navrow__sub">The link stops working at once</span>
                </span>
              </button>
            </div>
          </>
        ) : null}
        <div className="pj-pagepad" aria-hidden="true" />
      </div>
    </div>
  );
}
